import { useMemo } from 'react';
import { useQueries } from 'react-query';
import dataAPI from '../../../Jouly/services/dataAPI';
import { Point } from '../../../Jouly/types/types';
import { GridElement } from '../types';
import { MeterLiveMetric, MeterLiveMetricsMap } from '../types/presenter.types';

const OFFLINE_THRESHOLD_MS = 15 * 60 * 1000;
const REFETCH_INTERVAL_MS = 30_000;
// Hors-ligne: refetch lent (récupération online bornée) plutôt que toutes les 30s.
// La dernière valeur étant historique, on évite ainsi le walk-back répété.
const OFFLINE_REFETCH_INTERVAL_MS = 5 * 60 * 1000;
const STALE_TIME_MS = 5 * 60 * 1000;
const CACHE_TIME_MS = 30 * 60 * 1000;

// Intervalle de refetch selon l'état: 30s si en ligne, lent sinon.
const refetchByStatus = (data?: { lastSeenMs: number | null }): number => {
  const online =
    !!data && data.lastSeenMs !== null && Date.now() - data.lastSeenMs < OFFLINE_THRESHOLD_MS;
  return online ? REFETCH_INTERVAL_MS : OFFLINE_REFETCH_INTERVAL_MS;
};

const toFiniteNumber = (value: unknown): number | null => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return numericValue;
};

const getPointValue = (point?: Point): number | null => {
  if (!point) return null;
  const direct = toFiniteNumber(point.Value);
  if (direct !== null) return direct;
  return toFiniteNumber(point.Values?._value);
};

const getPointTimeMs = (point?: Point): number | null => {
  if (!point) return null;
  const raw = point.Time || point.Values?._time;
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
};

const getLastValue = (points: Point[] | undefined): number | null => {
  if (!points || points.length === 0) return null;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const v = getPointValue(points[i]);
    if (v !== null) return v;
  }
  return null;
};

const getLastPointTimeMs = (points: Point[] | undefined): number | null => {
  if (!points || points.length === 0) return null;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const t = getPointTimeMs(points[i]);
    if (t !== null) return t;
  }
  return null;
};

const isoDate = (offsetDays = 0): string => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

const resolveDeviceName = (element: GridElement): string | null => {
  if (element.type !== 'meter') return null;
  const fromNumber = element.number?.trim();
  if (fromNumber) return fromNumber;
  const fromMtrNumber = String(element.properties?.mtr_number ?? '').trim();
  if (fromMtrNumber) return fromMtrNumber;
  return null;
};

interface MeterTarget {
  elementId: string;
  deviceName: string;
}

interface RawResult {
  voltage: number | null;
  power: number | null;
  lastSeenMs: number | null;
}

const fetchMeterLive = async (deviceName: string): Promise<RawResult> => {
  const filter = {
    device_name: deviceName,
    startDate: isoDate(-1),
    stopDate: isoDate(0),
    aggregateWindow: '15m' as const,
  };

  const [voltageRes, powerRes, hbRes] = await Promise.allSettled([
    dataAPI.getLastVoltageSingleCounter(filter),
    dataAPI.getActivePowerPointSingleCounter(filter),
    dataAPI.getLastHeartbeatSingleCounter(filter),
  ]);

  const voltage = voltageRes.status === 'fulfilled' ? getLastValue(voltageRes.value) : null;
  const power = powerRes.status === 'fulfilled' ? getLastValue(powerRes.value) : null;
  const lastSeenMs = hbRes.status === 'fulfilled' ? getLastPointTimeMs(hbRes.value) : null;

  return { voltage, power, lastSeenMs };
};

export interface UseAllMetersLiveMetricsResult {
  metrics: MeterLiveMetricsMap;
  isLoading: boolean;
}

export const useAllMetersLiveMetrics = (
  elements: GridElement[],
  enabled: boolean,
  // Ids des compteurs affichés comme marqueur INDIVIDUEL sur la carte (dans la vue
  // ET hors cluster). null = inconnu (rien n'est requêté tant que la carte n'a pas
  // signalé l'état) pour éviter une rafale initiale sur tous les compteurs.
  visibleIds?: Set<string> | null,
): UseAllMetersLiveMetricsResult => {
  const targets: MeterTarget[] = useMemo(() => {
    return elements
      .filter((el) => el.type === 'meter')
      .map((el) => {
        const deviceName = resolveDeviceName(el);
        return deviceName ? { elementId: el.id, deviceName } : null;
      })
      .filter((t): t is MeterTarget => t !== null);
  }, [elements]);

  const queries = useQueries(
    targets.map((t) => ({
      queryKey: ['plan-bt-v2-all-meters-live', t.deviceName],
      queryFn: () => fetchMeterLive(t.deviceName),
      // Ne requêter que les compteurs affichés individuellement (les autres — hors
      // vue ou dans un cluster — gardent leur cache et ne sont pas re-collectés).
      enabled: enabled && Boolean(t.deviceName) && !!visibleIds && visibleIds.has(t.elementId),
      refetchInterval: (data?: RawResult) => refetchByStatus(data),
      staleTime: STALE_TIME_MS,
      cacheTime: CACHE_TIME_MS,
    })),
  );

  const dataUpdatedSignature = queries.map((q) => q.dataUpdatedAt).join(',');

  const metrics: MeterLiveMetricsMap = useMemo(() => {
    const now = Date.now();
    const map: MeterLiveMetricsMap = new Map();
    targets.forEach((t, idx) => {
      const data = queries[idx]?.data as RawResult | undefined;
      const lastSeenMs = data?.lastSeenMs ?? null;
      const isOnline = lastSeenMs !== null && now - lastSeenMs < OFFLINE_THRESHOLD_MS;
      const entry: MeterLiveMetric = {
        voltage: data?.voltage ?? null,
        power: data?.power ?? null,
        lastSeenMs,
        isOnline,
      };
      map.set(t.elementId, entry);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, dataUpdatedSignature]);

  const isLoading = queries.some((q) => q.isLoading);

  return { metrics, isLoading };
};

export default useAllMetersLiveMetrics;
